import React, { useEffect, useState } from "react";
import "./Feed.css";
import Playlist from "./Playlist";
import PlaylistView from "./PlaylistView";
import LyricFeed from "./LyricFeed";
import LyricUploadForm from "./LyricUploadForm";
import TrackDetail from "./TrackDetail";
import Friends from "./FriendsNew";
import { reviewsService } from "../services/reviewsService";
import { spotifyService } from "../services/spotifyService";
import { deezerService } from "../services/deezerService";
import { itunesService } from "../services/itunesService";
import deezerAPI from "./deezerApi";

const API_KEY_LASTFM = "3298485f4ffe657f98a945853cb61437";

const Feed = ({ onNavigate, playlists, setPlaylists, openPlaylistModal }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("Feed");
  const [playingUrl, setPlayingUrl] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [lyricsView, setLyricsView] = useState("feed"); // 'feed' o 'upload' para Lyrics
  const [songRatings, setSongRatings] = useState({}); // Almacenar ratings de canciones

  const [ratingsData, setRatingsData] = useState({
    topRated: [],
    recent: [],
    byGenre: {},
    loading: true
  });

  const [showFloatingActions, setShowFloatingActions] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // 1️⃣ Traer canciones de Last.fm con imagen de fallback
  const fetchCategoryLastFM = async (tag, limit = 30) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const res = await fetch(
        `https://ws.audioscrobbler.com/2.0/?method=tag.gettoptracks&tag=${encodeURIComponent(
          tag
        )}&limit=${limit}&api_key=${API_KEY_LASTFM}&format=json`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);
      const data = await res.json();

      if (!data.tracks?.track) {
        console.warn(`⚠️ No se encontraron canciones para ${tag}`);
        return [];
      }

      return data.tracks.track.map((t) => {
        // Obtener imagen de Last.fm como fallback
        let fallbackImage = null;
        if (t.image && Array.isArray(t.image)) {
          const largeImage = t.image.find(img => img.size === 'extralarge' || img.size === 'large');
          if (largeImage && largeImage['#text']) {
            fallbackImage = largeImage['#text'];
          }
        }

        return {
          title: t.name,
          artist: t.artist.name,
          fallbackImage: fallbackImage
        };
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        console.error(`⏱️ Timeout en Last.fm para ${tag}`);
      } else {
        console.error(`❌ Error Last.fm para ${tag}:`, e);
      }
      return [];
    }
  };

  // 2️⃣ Obtener portada desde iTunes y preview desde Deezer (en paralelo para mayor velocidad)
  const fetchTrackData = async (title, artist, fallbackImage) => {
    // Ejecutar ambas peticiones en paralelo para mayor velocidad
    const [itunesData, deezerData] = await Promise.all([
      itunesService.searchTrack(title, artist).catch(() => null),
      deezerService.searchTrack(title, artist).catch(() => null)
    ]);

    let cover = fallbackImage;
    let preview = null;
    let link = null;

    // Usar portada de iTunes (mejor calidad)
    if (itunesData?.artworkUrl600) {
      cover = itunesData.artworkUrl600;
    } else if (deezerData?.cover) {
      // Fallback a Deezer si iTunes no tiene
      cover = deezerData.cover;
    }

    // Obtener preview y link de Deezer
    if (deezerData) {
      preview = deezerData.preview;
      link = deezerData.link;
    }

    return { cover, preview, link };
  };

  // Hook para detectar scroll y ocultar/mostrar navegación
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          // Solo ocultar si se ha hecho scroll hacia abajo más de 10px
          if (currentScrollY > lastScrollY && currentScrollY > 10) {
            setIsNavVisible(false);
          } else if (currentScrollY < lastScrollY) {
            setIsNavVisible(true);
          }

          setLastScrollY(currentScrollY);
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // 3️⃣ Cargar categorías y canciones (MÁXIMO PARALELISMO)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const categories = [
          { name: " Top Global", tag: "pop" },
          { name: " Tendencias", tag: "indie" },
          { name: " Pop", tag: "pop rock" },
          { name: " Rock", tag: "rock" },
          { name: " Hip Hop", tag: "hip-hop" },
          { name: " R&B", tag: "rnb" },
        ];

        // ⚡ PASO 1: Cargar TODAS las categorías de Last.fm EN PARALELO
        const categoriesData = await Promise.all(
          categories.map(async (cat) => ({
            name: cat.name,
            songs: await fetchCategoryLastFM(cat.tag, 20)
          }))
        );

        // ⚡ PASO 2: Procesar TODAS las canciones de TODAS las categorías EN PARALELO
        const results = await Promise.all(
          categoriesData.map(async (category) => {
            const songsToProcess = category.songs.slice(0, 10);

            // Procesar todas las canciones de esta categoría en paralelo (sin lotes)
            const songsWithImages = await Promise.all(
              songsToProcess.map(async (song) => {
                const trackData = await fetchTrackData(song.title, song.artist, song.fallbackImage);
                const songData = {
                  title: song.title,
                  artist: song.artist,
                  image: trackData.cover,
                  previewUrl: trackData.preview,
                  spotifyUrl: trackData.link || `https://open.spotify.com/search/${encodeURIComponent(song.title + " " + song.artist)}`
                };
                console.log('🎵 Canción en Feed:', {
                  title: songData.title,
                  hasPreview: !!songData.previewUrl,
                  previewUrl: songData.previewUrl
                });
                return songData;
              })
            );

            return { name: category.name, songs: songsWithImages };
          })
        );

        // Filtrar secciones vacías
        const sectionsWithSongs = results.filter(section => section.songs.length > 0);

        setSections(sectionsWithSongs);
        setLoading(false);
      } catch (err) {
        console.error("❌ Error cargando feed:", err);
        setError("Error al cargar el feed. Por favor recarga la página.");
        setLoading(false);
      }
    };

    load();
  }, []);

  const handlePlay = (url, trackId) => {
    // Si ya hay un audio reproduciéndose, pausarlo
    if (audioElement) {
      audioElement.pause();
    }

    // Si es la misma canción, pausar
    if (playingUrl === trackId) {
      setPlayingUrl(null);
      setAudioElement(null);
    } else {
      // Reproducir nueva canción
      const audio = new Audio(url);
      audio.play();
      setAudioElement(audio);
      setPlayingUrl(trackId);
      audio.onended = () => {
        setPlayingUrl(null);
        setAudioElement(null);
      };
    }
  };

  const fetchSongRating = async (title, artist) => {
    try {
      const songId = `${title}-${artist}`.replace(/\s+/g, '-');
      const rating = await reviewsService.obtenerPromedioEstrellas('track', songId);
      return rating;
    } catch (error) {
      console.error('Error obteniendo rating:', error);
      return { promedio: 0, total: 0 };
    }
  };

  const loadAllRatings = async () => {
    const ratingPromises = [];
    const songKeys = [];

    for (const section of sections) {
      for (const song of section.songs) {
        const songKey = `${song.title}-${song.artist}`.replace(/\s+/g, '-');
        songKeys.push(songKey);
        ratingPromises.push(fetchSongRating(song.title, song.artist));
      }
    }

    const ratingsResults = await Promise.all(ratingPromises);

    const ratings = {};
    ratingsResults.forEach((rating, index) => {
      ratings[songKeys[index]] = rating;
    });

    setSongRatings(ratings);
  };

  useEffect(() => {
    if (sections.length > 0) {
      loadAllRatings();
    }
  }, [sections]);

  // Función para obtener imagen de alta calidad de Deezer
  // Obtener imagen de alta calidad de Deezer
const fetchHighQualityImage = async (title, artist) => {
  try {
    const result = await deezerAPI.searchTracks(`${title} ${artist}`, 1);
    if (result && result.data && result.data.length > 0) {
      const track = result.data[0];
      return track.album.cover_big || track.album.cover_medium;
    }
    return null;
  } catch (error) {
    console.error("Error obteniendo imagen de Deezer:", error);
    return null;
  }
};

// Obtener género de Last.fm
const fetchGenreFromLastFM = async (title, artist) => {
  try {
    const res = await fetch(
      `https://ws.audioscrobbler.com/2.0/?method=track.getInfo&artist=${encodeURIComponent(
        artist
      )}&track=${encodeURIComponent(
        title
      )}&api_key=${API_KEY_LASTFM}&format=json`
    );
    const data = await res.json();
    
    if (data.track?.toptags?.tag && data.track.toptags.tag.length > 0) {
      // Obtener el primer tag como género
      const genre = data.track.toptags.tag[0].name;
      // Capitalizar primera letra
      return genre.charAt(0).toUpperCase() + genre.slice(1);
    }
    return 'Varios';
  } catch (error) {
    console.error("Error obteniendo género de Last.fm:", error);
    return 'Varios';
  }
};

  const loadRatingsSection = async () => {
    try {
      setRatingsData(prev => ({ ...prev, loading: true }));
      
      const allReviews = await reviewsService.obtenerTodosLosReviews();
      
      if (!allReviews || allReviews.length === 0) {
        setRatingsData({
          topRated: [],
          recent: [],
          byGenre: {},
          loading: false
        });
        return;
      }

      const songMap = {};
      allReviews.forEach(review => {
        const key = review.entity_id;
        if (!songMap[key]) {
          songMap[key] = {
            entity_id: key,
            entity_name: review.entity_name,
            entity_artist: review.entity_artist,
            entity_image: review.entity_image,
            ratings: [],
            lastReview: review.created_at
          };
        }
        songMap[key].ratings.push(review.rating);
        if (new Date(review.created_at) > new Date(songMap[key].lastReview)) {
          songMap[key].lastReview = review.created_at;
        }
      });

      // Obtener imágenes de alta calidad para cada canción
      const songsWithRatings = await Promise.all(
  Object.values(songMap).map(async song => {
    const [highQualityImage, genre] = await Promise.all([
      fetchHighQualityImage(song.entity_name, song.entity_artist),
      fetchGenreFromLastFM(song.entity_name, song.entity_artist)
    ]);
    
    return {
      ...song,
      entity_image: highQualityImage || song.entity_image,
      genre: genre || 'Varios',
      averageRating: song.ratings.reduce((a, b) => a + b, 0) / song.ratings.length,
      totalReviews: song.ratings.length
    };
  })
);//fin songsWithRatings

      const topRated = songsWithRatings
        .filter(s => s.totalReviews >= 1)
        .sort((a, b) => b.averageRating - a.averageRating)
        .slice(0, 10);

      const recent = songsWithRatings
        .sort((a, b) => new Date(b.lastReview) - new Date(a.lastReview))
        .slice(0, 10);

      const byGenre = {};
      songsWithRatings.forEach(song => {
        const genre = song.genre || 'Varios'; // Usar el género de Deezer
        if (!byGenre[genre]) {
          byGenre[genre] = [];
        }
  
        const exists = byGenre[genre].some(s => s.entity_id === song.entity_id);
        if (!exists) {
          byGenre[genre].push(song);
        }
      });

      const topGenres = Object.entries(byGenre)
        .sort(([, a], [, b]) => b.length - a.length)
        .slice(0, 4)
        .reduce((obj, [key, value]) => {
          obj[key] = value.slice(0, 6);
          return obj;
        }, {});

      setRatingsData({
        topRated,
        recent,
        byGenre: topGenres,
        loading: false
      });
    } catch (error) {
      console.error('Error cargando ratings:', error);
      setRatingsData({
        topRated: [],
        recent: [],
        byGenre: {},
        loading: false
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'Ratings') {
      loadRatingsSection();
    }
  }, [activeTab]);

  const handleMouseEnter = () => {
    setShowFloatingActions(true);
  };

  const handleMouseLeave = () => {
    setShowFloatingActions(false);
  };

  const handleLyrics = () => {
    setActiveTab("Lyrics");
    setLyricsView("upload");
    setShowFloatingActions(false);
  };

  const handleLyricUploaded = () => {
    setLyricsView("feed");
  };

  const handleOpenPlaylistModal = (song = null) => {
    if (openPlaylistModal) {
      openPlaylistModal(song);
    }
    setShowFloatingActions(false);
  };

  const handleOpenReview = (song) => {
    const adaptedTrack = {
      id: song.entity_id || `${song.entity_name || song.title}-${song.entity_artist || song.artist}`.replace(/\s+/g, '-'),
      title: song.entity_name || song.title,
      artist: {
        name: song.entity_artist || song.artist
      },
      album: {
        title: song.entity_name || song.title,
        cover_medium: song.entity_image || song.image,
        cover_small: song.entity_image || song.image
      },
      preview: song.previewUrl,
      duration: 30
    };

    setSelectedTrack(adaptedTrack);
  };

  const handleCloseReview = () => {
    setSelectedTrack(null);
    if (sections.length > 0) {
      loadAllRatings();
    }
    if (activeTab === 'Ratings') {
      loadRatingsSection();
    }
  };

  const RatingCard = ({ song }) => (
    <div className="rating-card" onClick={() => handleOpenReview(song)}>
      <div className="rating-card-image">
        <img src={song.entity_image || 'https://via.placeholder.com/150'} alt={song.entity_name} />
        <div className="rating-overlay">
          <span className="rating-score">⭐ {song.averageRating.toFixed(1)}</span>
        </div>
      </div>
      <div className="rating-card-info">
        <div className="rating-card-title">{song.entity_name}</div>
        <div className="rating-card-artist">{song.entity_artist}</div>
        <div className="rating-card-stars">
          {'⭐'.repeat(Math.round(song.averageRating))}
        </div>
      </div>
    </div>
  );

  if (loading) return <div className="feed-loading">🎧 Cargando música...</div>;

  if (error) {
    return (
      <div className="feed-loading" style={{ flexDirection: 'column', gap: '20px' }}>
        <div style={{ fontSize: '48px' }}>😕</div>
        <div>{error}</div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(90deg, #00264b, #004080)',
            color: 'white',
            border: '2px solid #004080',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          Recargar Página
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="feed-outer">
        <div className="feed-header">
          {["Feed", "Friends", "Ratings", "Playlist", "Lyrics"].map((tab) => (
            <div
              key={tab}
              className={`tab ${activeTab === tab ? "active-tab" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </div>
          ))}
        </div>

        {activeTab === "Lyrics" && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            padding: '15px',
            backgroundColor: '#1a1a1a',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <button
              onClick={() => setLyricsView('feed')}
              style={{
                flex: 1,
                backgroundColor: lyricsView === 'feed' ? '#2563eb' : 'transparent',
                color: 'white',
                border: lyricsView === 'feed' ? 'none' : '2px solid #2563eb',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '15px',
                transition: 'all 0.3s ease'
              }}
            >
              Ver Feed
            </button>
            <button
              onClick={() => setLyricsView('upload')}
              style={{
                flex: 1,
                backgroundColor: lyricsView === 'upload' ? '#2563eb' : 'transparent',
                color: 'white',
                border: lyricsView === 'upload' ? 'none' : '2px solid #2563eb',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '15px',
                transition: 'all 0.3s ease'
              }}
            >
              + Subir Lyric
            </button>
          </div>
        )}

        {activeTab === "Feed" && (
          <>
            <div className="feed-categories">
              {sections.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '18px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎵</div>
                  <div>No se pudieron cargar las canciones.</div>
                  <div style={{ fontSize: '14px', marginTop: '10px' }}>
                    Verifica tu conexión a internet e intenta recargar.
                  </div>
                </div>
              ) : (
                sections.map((section, sIdx) => (
                  <div className="category-section" key={sIdx}>
                    <h2 className="category-title">{section.name}</h2>
                    {section.songs.length === 0 ? (
                      <div style={{
                        color: 'rgba(255,255,255,0.6)',
                        padding: '20px',
                        fontSize: '14px'
                      }}>
                        No hay canciones disponibles en esta categoría
                      </div>
                    ) : (
                      <div className="albums-row">
                        {section.songs.map((song, aIdx) => (
                      <div className="album-card-wrapper" key={aIdx}>
                        <div className="album-card">
                          <div className="album-image-container">
                            {song.image ? (
                              <img
                                src={song.image}
                                alt={song.title}
                                className="album-image"
                                onError={(e) => {
                                  console.error(`❌ Error cargando imagen para "${song.title}":`, song.image);
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="album-image" style={{
                                width: '100%',
                                aspectRatio: '1',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                padding: '10px'
                              }}>
                                {song.title.substring(0, 20)}
                              </div>
                            )}
                            {/* Overlay con botón de play */}
                            {song.previewUrl && (
                              <div
                                className="play-overlay"
                                onClick={() => handlePlay(song.previewUrl, `${song.title}-${song.artist}`)}
                              >
                                <div className="play-button">
                                  {playingUrl === `${song.title}-${song.artist}` ? "⏸" : "▶"}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="album-meta">
                            <div className="album-title">
                              {song.title}
                              {(() => {
                                const songKey = `${song.title}-${song.artist}`.replace(/\s+/g, '-');
                                const rating = songRatings[songKey];
                                if (rating && rating.total > 0) {
                                  return (
                                    <span className="song-rating">
                                      ⭐ {rating.promedio.toFixed(1)}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <div className="album-artist">{song.artist}</div>
                          </div>

                          <div className="track-actions">
                            <button
                              className="action-button btn-rate"
                              onClick={() => handleOpenReview(song)}
                            >
                              ⭐ Calificar
                            </button>
                            <button
                              className="action-button btn-playlist"
                              onClick={() => handleOpenPlaylistModal(song)}
                            >
                              ➕ Playlist
                            </button>
                          </div>
                        </div>

                        {song.spotifyUrl && (
                          <a href={song.spotifyUrl} target="_blank" rel="noopener noreferrer" className="listen-full-button">
                            Escuchar Completa
                          </a>
                        )}
                        </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {activeTab === "Friends" && <Friends />}

        {activeTab === "Playlist" && (
          <PlaylistView
            playlists={playlists}
            setPlaylists={setPlaylists}
            openPlaylistModal={openPlaylistModal}
          />
        )}

        {activeTab === "Ratings" && (
          <div className="ratings-section">
            {ratingsData.loading ? (
              <div className="feed-loading">⭐ Cargando ratings...</div>
            ) : (
              <>
                {ratingsData.topRated.length > 0 && (
                  <div className="ratings-category">
                    <h2 className="ratings-title">⭐ Top Rated</h2>
                    <div className="ratings-grid">
                      {ratingsData.topRated.map((song, idx) => (
                        <RatingCard key={idx} song={song} />
                      ))}
                    </div>
                  </div>
                )}

                {ratingsData.recent.length > 0 && (
                  <div className="ratings-category">
                    <h2 className="ratings-title">🕒 Recent</h2>
                    <div className="ratings-grid">
                      {ratingsData.recent.map((song, idx) => (
                        <RatingCard key={idx} song={song} />
                      ))}
                    </div>
                  </div>
                )}

                {Object.keys(ratingsData.byGenre).length > 0 && (
                  <>
                    {Object.entries(ratingsData.byGenre).map(([genre, songs]) => (
                      <div className="ratings-category" key={genre}>
                        <h2 className="ratings-title">🎵 {genre}</h2>
                        <div className="ratings-grid">
                          {songs.map((song, idx) => (
                            <RatingCard key={idx} song={song} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {ratingsData.topRated.length === 0 && ratingsData.recent.length === 0 && (
                  <div className="empty-ratings">
                    <h3>🎵 No hay canciones calificadas aún</h3>
                    <p>¡Sé el primero en calificar música!</p>
                    <button 
                      className="action-button btn-rate"
                      onClick={() => setActiveTab('Feed')}
                    >
                      Explorar Música
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "Lyrics" && (
          <div style={{ padding: '20px' }}>
            {lyricsView === 'feed' ? (
              <LyricFeed />
            ) : (
              <LyricUploadForm onSuccess={handleLyricUploaded} />
            )}
          </div>
        )}
      </div>

      <div className={`floating-nav ${!isNavVisible ? 'hidden' : ''}`}>
        <button
          className="nav-icon"
          onClick={() => setActiveTab("Feed")}
        >
          <span className="icon">🏠</span>
          <span className="icon-label">Inicio</span>
        </button>

        <button
          className="nav-icon"
          onClick={() => onNavigate && onNavigate('search')}
        >
          <span className="icon">🔍</span>
          <span className="icon-label">Buscar</span>
        </button>

        <div
          className="nav-center-container"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {showFloatingActions && (
            <div className="floating-actions">
              <button
                className="floating-action-btn"
                onClick={() => handleOpenPlaylistModal(null)}
              >
                <span className="floating-action-icon">🎵</span>
                <span className="floating-action-label">Playlist</span>
              </button>
              <button
                className="floating-action-btn"
                onClick={handleLyrics}
              >
                <span className="floating-action-icon">🎤</span>
                <span className="floating-action-label">Lyrics</span>
              </button>
            </div>
          )}

          <button
            className={`nav-icon nav-icon-add ${showFloatingActions ? 'rotated' : ''}`}
          >
            <span className="icon icon-plus">➕</span>
          </button>
        </div>

        <button
          className="nav-icon"
          onClick={() => alert("Notificaciones próximamente")}
        >
          <span className="icon">❤️</span>
          <span className="icon-label">Likes</span>
        </button>

        <button
          className="nav-icon"
          onClick={() => onNavigate && onNavigate('profile')}
        >
          <span className="icon">👤</span>
          <span className="icon-label">Mi Perfil</span>
        </button>
      </div>

      {selectedTrack && (
        <TrackDetail
          track={selectedTrack}
          onClose={handleCloseReview}
        />
      )}
    </>
  );
};

export default Feed;